import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';

const TeamRcaList = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [userId, setUserId] = useState(authState.user._id);
  const [rcas, setRcas] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [teamCode, setTeamCode] = useState('');
  const [rcaType, setRcaType] = useState('');
  const [rcaTypeHead, setRcaTypeHead] = useState('');
  const rcaTypeInitialized = useRef(false);
  const [selectedRcaId, setSelectedRcaId] = useState(null);
  const [teamCodeMsg, setTeamCodeMsg] = useState('');

  const fetchTeamRcas = useCallback((rcaType) => {
    if (!rcaType) {
        return;
    }
    
    const config = {
        url: "/rca/myteamrca",
        method: "post",
        headers: { Authorization: authState.token },
        data: { rcaType: rcaType, userId: userId, teamCode: teamCode  }
    };
    // console.log("Getting "+rcaType+" RCA",config);

    
    fetchData(config, { showSuccessToast: false })
        .then(response => {
            // console.log("Response:", response);
            if (response && Array.isArray(response.usersrca)) {
                setRcas(response.usersrca);
            } else {
                console.error("Invalid response format: usersrca not found in response");
            }
        })
        .catch(error => {
            console.error("Error fetching rcas:", error);
        });
  }, [authState.token, fetchData, rcaType, userId, teamCode]);

  useEffect(() => {
      if (!authState.isLoggedIn) return;
      fetchTeamRcas();
    }, [authState.isLoggedIn, fetchTeamRcas]);

    useEffect(() => {
      const rcaTypeFromRoute = (location?.pathname || '').split('/').pop() || '';
      if (rcaTypeFromRoute && !rcaTypeInitialized.current) {
        let updatedRcaType = '';
        let rcaTypeHead = '';
        switch (rcaTypeFromRoute) {
          case 'team-concerns-list':
            updatedRcaType = 'Challenge';
            rcaTypeHead = "Incident/Issue/Challenge/Problem";
            break;
          case 'team-objectives-list':
            updatedRcaType = 'Goal';
            rcaTypeHead = "Goal/Decision";
            break;
          default:
            updatedRcaType = 'Challenge';
            rcaTypeHead = "Incident/Issue/Challenge/Problem";
            break;
        }
        setRcaType(updatedRcaType);
        setRcaTypeHead(rcaTypeHead);
        rcaTypeInitialized.current = true;
      }
    }, [location.pathname]);

    // useEffect(() => {
    //   if (rcaType) {
    //     fetchTeamRcas(rcaType);
    //   }
    // }, [rcaType, fetchTeamRcas]);

    const handleDelete = (id) => {
      const config = { url: `/rca/${id}`, method: "delete", headers: { Authorization: authState.token } };
      fetchData(config).then(() => {
        setRcas(rcas.filter(rca => rca._id !== id));
      }).catch(error => {
        console.error('Failed to delete the record:', error);
      });
    }

    const handleView = (rcaId) => {
      setSelectedRcaId(prevId => (prevId === rcaId ? null : rcaId));
    };

    const handleCloseRcaDetails = () => {
      setSelectedRcaId(null);
    };

    const handleFromDateChange = (date) => {
      setFromDate(date);
    };

    const handleToDateChange = (date) => {
      setToDate(date);
    };

    const handleSearch = () => {
      if (teamCode === '') {
        setTeamCodeMsg("Team Code is Required");
        return false;
      }
      if (teamCode.length < 14) {
        setTeamCodeMsg("Team Code must be 14 characters");
        return false;
      }
      if (!teamCode.startsWith('TT')) {
        setTeamCodeMsg("Team Code must start with TT");
        return false;
      }
      fetchTeamRcas(rcaType);
    };

    const handleRcaClick = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const handleRcaShare = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const handleRcaSave = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const handleRcaPrint = (rcaId) => {
      setSelectedRcaId(rcaId);
    };

    const formatDate = (date) => {
      const jsDate = new Date(date);
      const day = jsDate.getDate().toString().padStart(2, '0');
      const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jsDate.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formatDateTime = (date) => {
      const options = {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false
      };
      const jsDate = new Date(date);
      const day = jsDate.getDate().toString().padStart(2, '0');
      const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jsDate.getFullYear();
      const hours = jsDate.getHours().toString().padStart(2, '0');
      const minutes = jsDate.getMinutes().toString().padStart(2, '0');
      const seconds = jsDate.getSeconds().toString().padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const sortedRcas = [...rcas].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  return (
    <div className='bg-black'>
        <div className='border-b border-b-blue-400'>
          <h2 className='text-xl lg:text-2xl mt-1 py-4 mx-8 text-yellow-200'>5Whys Analysis - My Team RCA List</h2>
        </div>
        <div className="min-h-[80vh] flex flex-col justify-center items-center my-2 mx-auto py-4">
          <div className="search-container w-full py-2 px-4 flex justify-center items-center">
            <div className="search-row flex justify-center items-center">
              <input 
                type="text" 
                placeholder="Team Code" 
                value={teamCode} 
                onChange={(e) => setTeamCode(e.target.value)} 
                style={{ width: '200px'}} 
              />
              <button className="small-button" onClick={handleSearch}>Get List</button>
            </div>
            <p className='text-red-400 ml-4 texl-xl'>{teamCodeMsg}</p>
          </div>
          <div className="p-4 mt-4">
            <div className="mt-2">
              <div className="mt-2">
                {rcas && rcas.length !== 0 && (
                  <div className="flex justify-between items-center border-b border-b-gray-300">
                    <div>
                    <h3 className='text-xl mt-1 mx-2 text-white'>{rcas.length}{' '}{rcaTypeHead} Found!</h3>
                    </div>
                    <div className='mr-1'>
                    <Link to='/rcas/add' className='block w-8 h-full px-2 py-1 ml-4 bg-blue-500'>
                      <FontAwesomeIcon className="bg-blue-500" icon={faPlus} />
                    </Link>
                  </div>
                </div>
                )}
              </div>
            </div>
            {loading ? (
              <Loader />
            ) : (
              <div className='text-sm'>
                {rcas && rcas.length === 0 ? (
                  <div className='h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                    <div className="flex flex-col justify-between items-center">
                      <div>
                        <h3 className='text-lg lg:text-xl mt-1 mx-2 text-yellow-200'>No Team {rcaTypeHead} Record Found!</h3>
                      </div>
                    <div>
                    <Link to='/rcas/add' className='block w-full h-full px-1 py-2 ml-4 bg-blue-600 rounded-lg'>
                      <FontAwesomeIcon className="bg-blue-500 mr-4" icon={faPlus} />  Add New
                    </Link>
                  </div>
                </div>
                  </div>
                ) : (
                  sortedRcas.map((rca, index) => (
                    <div key={rca._id} className='bg-black text-white my-1 p-2 rounded-md shadow-md'>
                      <div className='flex'>
                        <span className='font-medium'>#{index + 1} ({formatDateTime(rca.dateCreated)})</span>
                        <Tooltip text={"View details"} position={"top"}>
                          <span className='ml-auto mr-2 text-blue-500 cursor-pointer' onClick={() => handleView(rca._id)}>
                            <i className="fa-solid fa-eye"></i>
                          </span>
                        </Tooltip>
                        <Tooltip text={"Edit this record"} position={"top"}>
                          <Link to={`/rcas/${rca._id}`} className='mr-2 text-green-600 cursor-pointer'>
                            <i className="fa-solid fa-pen"></i>
                          </Link>
                        </Tooltip>
                        <Tooltip text={"Delete this record"} position={"top"}>
                          <span className='text-red-500 cursor-pointer mr-2' onClick={() => handleDelete(rca._id)}>
                            <i className="fa-solid fa-trash"></i>
                          </span>
                        </Tooltip>
                      </div>
                      <div className='flex flex-wrap text-blue-700'>{rca.rcaType} : {rca.concernArea}</div>
                      {selectedRcaId === rca._id && (
                        <div className='bg-black text-white'>
                          <h2 className='text-yellow-400 text-lg lg:text-xl'><strong>Team {rca.rcaType} Details</strong></h2>
                          <p><strong>Team Name:</strong> {rca.teamName}</p>

                          <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>5Whys Analysis</strong>
                          </p> 
                          <p><strong>Concern Area:</strong> {rca.concernArea}</p>
                          <p><strong>Problem Statement:</strong> {rca.problemStatement}</p>
                          <p><strong>Why1:</strong> {rca.why1}</p>
                          <p><strong>Why2:</strong> {rca.why2}</p>
                          <p><strong>Why3:</strong> {rca.why3}</p>
                          <p><strong>Why4:</strong> {rca.why4}</p>
                          <p><strong>Why5:</strong> {rca.why5}</p>
                          <p><strong>Root Cause:</strong> {rca.rootCause}</p>
                           <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>Solutions & Impact</strong>
                          </p> 
                          <p><strong>Suggested Solutions:</strong> {rca.quickSolutionsNote}</p>
                          <p><strong>Selected Solution:</strong> {rca.quickSolutionsSelectionNote}</p>
                          <p><strong>Solution Actions:</strong> {rca.quickSolutionsActionsNote}</p>
                          <p><strong>Imapct Note:</strong> {rca.quickImpactNotes}</p>
                           <p className='text-yellow-200 justify-center text-m mt-2 mb-2'>
                            <strong>Other Info</strong>
                          </p> 
                          <p><strong>RCA Type:</strong> {rca.rcaType}</p>
                          <p><strong>Date Created:</strong> {formatDateTime(rca.dateCreated)}</p>
                          <p><strong>Team's Own RCA ID:</strong> {rca.userRCAID}</p>
                          <p><strong>Think5Whys RCA Code:</strong> {rca.rcaCode}</p>
                          <p><strong>Think5Whys Team Code:</strong> {rca.teamCode}</p>
                          <p><strong>Category:</strong> {rca.category}</p>
                          <p><strong>Sub-Category:</strong> {rca.subCategory}</p>
                          <p><strong>Summary:</strong> {rca.summary}</p>
                          <p><strong>Remarks:</strong> {rca.remarks}</p>
                          <p className='mb-4'><strong>Tags:</strong> {rca.tags}</p>
                      
                          <Tooltip text={"Share"} position={"top"}>
                            <span className='text-green-500 cursor-pointer mr-4' disabled={true} onClick={() => handleRcaShare(rca._id)}>
                              <i className="fa-solid fa-share"></i>
                            </span>
                          </Tooltip>
                          <Tooltip text={"Save"} position={"top"}>
                            <span className='text-blue-500 cursor-pointer mr-4' disabled={true} onClick={() => handleRcaSave(rca._id)}>
                              <i className="fa-solid fa-save"></i>
                            </span>
                          </Tooltip>
                          <Tooltip text={"Print"} position={"top"}>
                            <span className='text-orange-500 cursor-pointer mr-4' disabled={true} onClick={() => handleRcaPrint(rca._id)}>
                              <i className="fa-solid fa-print"></i>
                            </span>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <footer className="bg-gray-900 border-t border-blue-400 text-gray-400 py-4">
          <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about-us" className="hover:text-teal-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
          </div>
        </footer>           
      </div>
  )
}

export default TeamRcaList

